import React, {useCallback, useEffect, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import './AttachFiles.css';
import {Trans, useTranslation} from 'react-i18next';
import {ErrorMessage} from 'formik';
import classnames from 'classnames';
import _get from 'lodash/get';
import {toast} from 'react-toastify';

const maxNumber = 3;
const fiveMBInBytes = 5242880;
const acceptedTypes = '.xml, .txt, .pdf, .jpg, .jpeg, .gif, .png, .zip, .rar';

export default function AttachFiles({form, field}) {
  const {t} = useTranslation();
  const [files, setFiles] = useState([]);
  const enableToUpload = files.length < maxNumber;

  useEffect(() => {
    setFiles(field.value);
  }, [field.value]);

  const getFileIcon = (fileName = '') => {
    const ext = fileName.substr(fileName.lastIndexOf('.'), fileName.length);
    switch (ext) {
      case '.jpg':
      case '.jpeg':
      case '.gif':
      case '.png':
        return require('../../../assets/images/contactus/attach_img.png');
      case '.zip':
      case '.rar':
        return require('../../../assets/images/contactus/attach_zip.png');
      case '.xml':
      case '.txt':
      case '.pdf':
      default:
        return require('../../../assets/images/contactus/attach_file.png');
    }
  };

  const handleRemove = useCallback(
    index => {
      const attachedFiles = files;
      attachedFiles.splice(index, 1);
      setFiles(attachedFiles);
      form.setFieldValue(field.name, attachedFiles);
    },
    [files, form, field]
  );

  const handleDrop = useCallback(
    acceptedFiles => {
      const newAddedCount = acceptedFiles.length;
      const originalCount = files.length;
      if (newAddedCount + originalCount > maxNumber) {
        return toast.error(
          t(
            'p.c.addrequest.attach.files.max.number',
            'Maximum 3 attached files.'
          )
        );
      }
      const attachedFiles = files.concat(acceptedFiles);
      setFiles(attachedFiles);
      form.setFieldValue(field.name, attachedFiles);
    },
    [files, form, field]
  );

  const {getRootProps, getInputProps} = useDropzone({
    maxSize: fiveMBInBytes,
    accept: acceptedTypes,
    multiple: true,
    onDrop: handleDrop,
    disabled: !enableToUpload,
    onDropRejected: rejectedFiles => {
      const rejectedFile = rejectedFiles[0];
      if (rejectedFile.size > fiveMBInBytes) {
        return toast.error(
          t(
            'p.c.addrequest.attach.files.size.limit',
            'File is too large. Size limit 5MB.'
          )
        );
      }
      toast.error(
        t(
          'p.c.addrequest.attach.files.unsupported.format',
          'Unsupported file format.'
        )
      );
    },
  });

  return (
    <>
      <div
        {...getRootProps()}
        className={classnames({
          'attach-files-block': true,
          'form-control': true,
          'is-invalid':
            _get(form.touched, field.name) && _get(form.errors, field.name),
        })}
      >
        <input {...getInputProps()} />
        {files.map((file, index) => {
          return (
            <div
              key={index}
              title={file.name}
              className="attach-files-block-item"
            >
              <img
                alt="icon"
                className="attach-files-block-item__icon"
                src={getFileIcon(file.name)}
              />
              {file.name}
              <button
                type="button"
                className="attach-files-block-item__remove btn btn-outline-secondary btn-icon btn-sm"
                onClick={e => {
                  e.stopPropagation();
                  handleRemove(index);
                }}
              >
                <i className="la la-remove" />
              </button>
            </div>
          );
        })}
        {enableToUpload && (
          <div className="text-muted kt-margin-5">
            <Trans i18nKey="p.c.addrequest.attach.files.hint">
              Drag & drop a file to attach it, <br />
              <span className="text-info">or browse for a file...</span>
            </Trans>
          </div>
        )}
      </div>
      <span className="invalid-feedback">
        <ErrorMessage name={field.name} />
      </span>
    </>
  );
}
